import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { useCallback, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarDivider,
  canvasToolbarButtonVariants,
} from "../components/toolbar";
import { canvasToolbarIconClassName } from "../components/toolbar/canvasToolbarIcon";
import { ArrowEndPicker, type ArrowEndStyle } from "./shared/ArrowEndPicker";
import { EDGE_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { LineStylePicker, type LineStyle } from "./shared/LineStylePicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

export type ConnectorCurveType = "straight" | "smooth" | "step";

/** Shared curve-type icon map — line shape only, no arrowheads. */
export const CURVE_TYPE_ICONS: Record<ConnectorCurveType, React.ReactNode> = {
  straight: (
    <svg
      className={canvasToolbarIconClassName}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <line
        x1="2"
        y1="14"
        x2="14"
        y2="2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  smooth: (
    <svg
      className={canvasToolbarIconClassName}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M 1 13 Q 2 3 14 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  ),
  step: (
    <svg
      className={canvasToolbarIconClassName}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <polyline
        points="1,13 7,13 7,3 14,3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  ),
};

const CURVE_OPTIONS: { value: ConnectorCurveType; label: string }[] = [
  { value: "straight", label: "Straight" },
  { value: "smooth", label: "Smooth" },
  { value: "step", label: "Step" },
];

export interface ConnectorStyle {
  color?: string;
  stroke_width?: number;
  stroke_style?: "solid" | "dashed" | "dotted";
  start_arrow?: ArrowEndStyle;
  end_arrow?: ArrowEndStyle;
  curve_type?: ConnectorCurveType;
  /** @deprecated use startArrow/endArrow instead */
  arrow_mode?: "none" | "end" | "both";
}

interface ConnectorFloatingToolbarProps {
  style: ConnectorStyle;
  onStyleChange: (style: ConnectorStyle) => void;
  onPopoverOpenChange?: (open: boolean) => void;
}

export function ConnectorFloatingToolbar({
  style,
  onStyleChange,
  onPopoverOpenChange,
}: ConnectorFloatingToolbarProps) {
  const [isCurveOpen, setIsCurveOpen] = useState(false);

  const isColorOpenRef = useRef(false);
  const isLineStyleOpenRef = useRef(false);
  const isStartOpenRef = useRef(false);
  const isEndOpenRef = useRef(false);
  const isCurveOpenRef = useRef(false);

  const notifyOpen = useCallback(
    (open: boolean) => {
      onPopoverOpenChange?.(
        isColorOpenRef.current ||
          isLineStyleOpenRef.current ||
          isStartOpenRef.current ||
          isEndOpenRef.current ||
          isCurveOpenRef.current ||
          open,
      );
    },
    [onPopoverOpenChange],
  );

  const handleColorOpen = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleLineStyleOpen = useCallback(
    (open: boolean) => {
      isLineStyleOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleStartOpen = useCallback(
    (open: boolean) => {
      isStartOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleEndOpen = useCallback(
    (open: boolean) => {
      isEndOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleCurveOpen = useCallback(
    (open: boolean) => {
      setIsCurveOpen(open);
      isCurveOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  // Migrate legacy arrowMode
  const legacyArrow = style.arrow_mode;
  const currentColor = style.color ?? "default";
  const currentStrokeStyle = style.stroke_style ?? "solid";
  const currentStrokeWidth = style.stroke_width ?? 2;
  const currentStartArrow: ArrowEndStyle =
    style.start_arrow ?? (legacyArrow === "both" ? "arrow" : "none");
  const currentEndArrow: ArrowEndStyle =
    style.end_arrow ?? (legacyArrow === "none" ? "none" : "arrow");
  const currentCurve = style.curve_type ?? "straight";

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame onMouseDown={(e) => e.preventDefault()}>
        {/* Color */}
        <CanvasColorPickerButton
          colors={EDGE_COLORS}
          activeValue={currentColor}
          tooltip="Color"
          onColorChange={(color) => onStyleChange({ color })}
          onOpenChange={handleColorOpen}
          emitField="swatchHex"
        />

        {/* Line style (style + width combined) */}
        <LineStylePicker
          value={{
            strokeStyle: currentStrokeStyle,
            strokeWidth: currentStrokeWidth,
          }}
          onChange={(ls: LineStyle) =>
            onStyleChange({
              stroke_style: ls.strokeStyle,
              stroke_width: ls.strokeWidth,
            })
          }
          onOpenChange={handleLineStyleOpen}
        />

        <CanvasToolbarDivider />

        {/* Start arrow */}
        <ArrowEndPicker
          value={currentStartArrow}
          onChange={(v) => onStyleChange({ start_arrow: v })}
          onOpenChange={handleStartOpen}
          label="Start"
          isStart
        />

        {/* Curve type */}
        <Popover open={isCurveOpen} onOpenChange={handleCurveOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className={canvasToolbarButtonVariants()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {CURVE_TYPE_ICONS[currentCurve]}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Curve type</TooltipContent>
          </Tooltip>
          <PopoverContent
            className="w-40 p-1 rounded-xl"
            align="center"
            side="top"
            sideOffset={14}
            onClick={(e) => e.stopPropagation()}
          >
            {CURVE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  onStyleChange({ curve_type: opt.value });
                  handleCurveOpen(false);
                }}
                className={cn(
                  "w-full px-3 py-1.5 flex items-center gap-2.5 rounded-lg transition-colors",
                  "hover:bg-n-95/8 dark:hover:bg-n-95/10",
                  currentCurve === opt.value &&
                    "bg-primary/10 text-primary font-medium",
                )}
              >
                {CURVE_TYPE_ICONS[opt.value]}
                <span className="text-xs">{opt.label}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {/* End arrow */}
        <ArrowEndPicker
          value={currentEndArrow}
          onChange={(v) => onStyleChange({ end_arrow: v })}
          onOpenChange={handleEndOpen}
          label="End"
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
